import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCircle2, LogOut, MessageSquare, Upload, User, XCircle } from 'lucide-react';
import type { ActivityItem } from '../../shared/types';
import { api, post } from '../lib/api';
import { activityText } from '../lib/activity';
import { initials, relativeTime } from '../lib/format';
import { isAgencyUser, useConfig, useMe } from '../lib/session';
import { Menu, Spinner } from './ui';

export function useTitle(title: string | undefined) {
  const { data: config } = useConfig();
  useEffect(() => {
    if (config) document.title = title ? `${title} · ${config.appName}` : config.appName;
  }, [title, config]);
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { data: me, isLoading } = useMe();
  const location = useLocation();
  if (isLoading) return <Spinner />;
  if (!me)
    return (
      <Navigate to={`/anmelden?weiter=${encodeURIComponent(location.pathname + location.search)}`} replace />
    );
  return <>{children}</>;
}

export function ActivityIcon({ type }: { type: string }) {
  if (type === 'decision_approved') {
    return (
      <span className="activity-icon ok">
        <CheckCircle2 size={17} />
      </span>
    );
  }
  if (type === 'decision_changes_requested') {
    return (
      <span className="activity-icon bad">
        <XCircle size={17} />
      </span>
    );
  }
  if (type === 'comment' || type === 'reply') {
    return (
      <span className="activity-icon">
        <MessageSquare size={16} />
      </span>
    );
  }
  return (
    <span className="activity-icon">
      <Upload size={16} />
    </span>
  );
}

export function activityLink(item: ActivityItem): string | null {
  if (!item.campaignId) return null;
  return item.bannerId
    ? `/kampagnen/${item.campaignId}/motiv/${item.bannerId}`
    : `/kampagnen/${item.campaignId}`;
}

function Notifications() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<{ unread: number; seenAt: string; items: ActivityItem[] }>('/api/notifications'),
    refetchInterval: 60_000,
  });
  const seen = useMutation({
    mutationFn: () => post('/api/notifications/seen'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const unread = data?.unread ?? 0;
  return (
    <Menu
      trigger={(props) => (
        <button
          {...props}
          className="btn btn-ghost btn-icon"
          aria-label={unread ? `${unread} neue Benachrichtigungen` : 'Benachrichtigungen'}
          style={{ position: 'relative' }}
        >
          <Bell size={19} />
          {unread > 0 && (
            <span className="count-badge" style={{ position: 'absolute', top: 2, right: 0 }}>
              {unread}
            </span>
          )}
        </button>
      )}
    >
      {(close) => (
        <div style={{ width: 380, maxWidth: 'calc(100vw - 32px)' }}>
          <div className="menu-head row">
            <strong className="grow">Benachrichtigungen</strong>
            {unread > 0 && (
              <button className="text-link small" onClick={() => seen.mutate()}>
                Alle gelesen
              </button>
            )}
          </div>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {!data?.items.length && (
              <p className="small muted" style={{ padding: '8px 10px 12px' }}>
                Noch nichts Neues.
              </p>
            )}
            {data?.items.map((item) => {
              const isNew = new Date(item.createdAt) > new Date(data.seenAt);
              const link = activityLink(item);
              return (
                <button
                  key={item.id}
                  className="menu-item"
                  style={{ alignItems: 'flex-start', background: isNew ? 'var(--accent-soft)' : undefined }}
                  onClick={() => {
                    close();
                    if (link) navigate(link);
                    if (unread) seen.mutate();
                  }}
                >
                  <ActivityIcon type={item.type} />
                  <span className="grow small" style={{ lineHeight: 1.4 }}>
                    <strong>{item.actorName}</strong> {activityText(item)}
                    <span className="tiny muted" style={{ display: 'block', marginTop: 2 }}>
                      {item.campaignName} · {relativeTime(item.createdAt)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Menu>
  );
}

function UserMenu() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  if (!me) return null;
  const roleLabel =
    me.role === 'admin' ? 'Admin' : me.role === 'member' ? 'Agentur' : (me.clientName ?? 'Kunde');
  return (
    <Menu
      trigger={(props) => (
        <button {...props} className="avatar" aria-label="Konto-Menü">
          {initials(me.name || me.email)}
        </button>
      )}
    >
      {(close) => (
        <>
          <div className="menu-head">
            <strong style={{ display: 'block' }}>{me.name || me.email}</strong>
            <span className="tiny muted">
              {me.email} · {roleLabel}
            </span>
          </div>
          <div className="menu-sep" />
          <Link className="menu-item" to="/profil" onClick={close}>
            <User size={16} /> Profil & Passwort
          </Link>
          <button
            className="menu-item"
            onClick={async () => {
              close();
              await post('/api/auth/logout');
              queryClient.clear();
              navigate('/anmelden');
            }}
          >
            <LogOut size={16} /> Abmelden
          </button>
        </>
      )}
    </Menu>
  );
}

/** Logo der Instanz (Admin-Bereich); auf dunklen Flächen die helle Variante oder invertiert. */
export function BrandLogo({ dark = false, style }: { dark?: boolean; style?: CSSProperties }) {
  const { data: config } = useConfig();
  const b = config?.branding;
  const src = (dark ? (b?.logoDarkUrl ?? b?.logoUrl) : b?.logoUrl) ?? '/theme/logo';
  return (
    <img
      className={`brand-logo ${dark && !b?.logoDarkUrl ? 'is-inverted' : ''}`}
      src={src}
      alt={b?.appName ?? ''}
      style={style}
    />
  );
}

export function AppShell() {
  const { data: me } = useMe();
  const { data: config } = useConfig();
  const agency = isAgencyUser(me);
  return (
    <>
      <header className="topbar">
        <div className="topbar-inner">
          <Link to="/" className="brand" aria-label={config?.appName}>
            <BrandLogo />
            {config?.branding.headerLabel && (
              <span className="brand-label">{config.branding.headerLabel}</span>
            )}
          </Link>
          <nav className="nav" aria-label="Hauptnavigation">
            <NavLink to="/" end>
              Kampagnen
            </NavLink>
            {agency && <NavLink to="/kunden">Kunden</NavLink>}
            {me?.role === 'admin' && <NavLink to="/team">Team</NavLink>}
            {me?.role === 'admin' && <NavLink to="/einstellungen">Einstellungen</NavLink>}
          </nav>
          <div className="topbar-actions">
            <Notifications />
            <UserMenu />
          </div>
        </div>
      </header>
      <main>
        <Outlet />
      </main>
    </>
  );
}

/** Fußzeile mit Rechtlichem, Kontakt und – abschaltbar – dem Hinweis auf ZeigBanner. */
/** Fuß mit Rechtlichem. `neutral`: ohne Namen und Kontakt der Agentur (Freigaben im Namen eines Partners). */
export function LegalFooter({ neutral = false }: { neutral?: boolean }) {
  const { data: config } = useConfig();
  if (!config) return null;
  const b = config.branding;
  return (
    <footer className="app-foot">
      {!neutral && <span>{b.providerName || b.appName}</span>}
      {!neutral && b.supportEmail && (
        <span>
          Fragen? <a href={`mailto:${b.supportEmail}`}>{b.supportEmail}</a>
        </span>
      )}
      {b.imprintUrl && (
        <a href={b.imprintUrl} target="_blank" rel="noreferrer">
          Impressum
        </a>
      )}
      {b.privacyUrl && (
        <a href={b.privacyUrl} target="_blank" rel="noreferrer">
          Datenschutz
        </a>
      )}
      {b.showPoweredBy && <span className="spacer" />}
      {b.showPoweredBy && <PoweredBy />}
    </footer>
  );
}

export function PoweredBy() {
  return <span className="tiny muted">Läuft mit ZeigBanner · Open Source von Chrustek Studio</span>;
}
